import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { listDocuments, deleteDocument } from "@/lib/documents.functions";
import { SiteShell } from "@/components/site-shell";
import { UsageBadge } from "@/components/usage-badge";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/use-auth";
import { formatDistanceToNow } from "date-fns";
import { Lock } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/dashboard")({
  head: () => ({
    meta: [{ title: "My Documents — DealDraft" }, { name: "robots", content: "noindex" }],
  }),
  component: DashboardPage,
});

function DashboardPage() {
  const { session } = useAuth();
  const headers = session?.access_token
    ? { Authorization: `Bearer ${session.access_token}` }
    : undefined;
  const list = useServerFn(listDocuments);
  const del = useServerFn(deleteDocument);
  const qc = useQueryClient();

  const docsQ = useQuery({
    queryKey: ["documents"],
    queryFn: () => list({ headers }),
    enabled: !!headers,
  });

  async function onDelete(id: string) {
    if (!confirm("Delete this document?")) return;
    await del({ data: { id }, headers });
    qc.invalidateQueries({ queryKey: ["documents"] });
    toast.success("Deleted");
  }

  const docs = docsQ.data ?? [];

  return (
    <SiteShell>
      <section className="mx-auto max-w-5xl px-6 py-10">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-semibold tracking-tight">My Documents</h1>
          <Button asChild>
            <Link to="/generate">New Document →</Link>
          </Button>
        </div>

        <div className="mt-6 lg:hidden">
          <UsageBadge />
        </div>

        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          <div className="rounded-xl border border-border bg-card p-5 shadow-sm">
            <h2 className="font-semibold">Generate</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Create a new AI proposal and contract in under 60 seconds.
            </p>
            <Button asChild className="mt-4" size="sm">
              <Link to="/generate">New Document →</Link>
            </Button>
          </div>
          <div className="relative rounded-xl border border-dashed border-border bg-muted/30 p-5 opacity-75">
            <span className="absolute right-4 top-4 rounded-full bg-accent/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-accent">
              Coming Soon
            </span>
            <div className="flex items-center gap-2">
              <Lock className="h-4 w-4 text-muted-foreground" />
              <h2 className="font-semibold text-muted-foreground">E-Signature</h2>
            </div>
            <p className="mt-2 text-sm text-muted-foreground">
              Send and collect legally binding signatures. Coming soon.
            </p>
          </div>
        </div>

        <div className="mt-8 space-y-3">
          {docsQ.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
          {!docsQ.isLoading && docs.length === 0 && (
            <div className="rounded-xl border border-dashed border-border p-12 text-center">
              <p className="text-lg font-medium">No documents yet.</p>
              <p className="mt-1 text-sm text-muted-foreground">Generate your first proposal →</p>
              <Button asChild className="mt-5">
                <Link to="/generate">Generate Now</Link>
              </Button>
            </div>
          )}
          {docs.map((d) => (
            <div
              key={d.id}
              className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-card p-4 shadow-sm"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <p className="truncate font-semibold">{d.project_title}</p>
                  <span className="rounded-full bg-secondary px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                    {d.project_type}
                  </span>
                </div>
                <p className="mt-0.5 text-sm text-muted-foreground">
                  {d.client_name} · ${Number(d.total_price).toLocaleString()} ·{" "}
                  {formatDistanceToNow(new Date(d.created_at), { addSuffix: true })}
                </p>
              </div>
              <div className="flex gap-2">
                <Button asChild variant="outline" size="sm">
                  <Link to="/documents/$id" params={{ id: d.id }}>
                    View
                  </Link>
                </Button>
                <Button variant="ghost" size="sm" onClick={() => onDelete(d.id)}>
                  Delete
                </Button>
              </div>
            </div>
          ))}
        </div>
      </section>
    </SiteShell>
  );
}
